import { useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Plus, Trash2, Loader2, MessageSquare, Crown, Sparkles } from "lucide-react";
import { SUGGESTED_QUESTIONS } from "@/data/staticData";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";

// Composant d'affichage progressif du texte (effet streaming)
function StreamingText({ content, onComplete }) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  const idx = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!content) return;
    idx.current = 0;
    setDisplayed("");
    setDone(false);

    const CHARS_PER_TICK = 3;
    const TICK_MS = 12;

    const tick = () => {
      idx.current = Math.min(idx.current + CHARS_PER_TICK, content.length);
      setDisplayed(content.slice(0, idx.current));
      if (idx.current < content.length) {
        rafRef.current = setTimeout(tick, TICK_MS);
      } else {
        setDone(true);
        onComplete?.();
      }
    };
    rafRef.current = setTimeout(tick, 50);

    return () => { if (rafRef.current) clearTimeout(rafRef.current); };
  }, [content, onComplete]);

  return (
    <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1.5 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-slate-900 prose-code:text-[var(--ia-blue)] prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs">
      <ReactMarkdown>{displayed}</ReactMarkdown>
      {!done && <span className="inline-block w-1.5 h-4 bg-[var(--ia-navy)] ml-0.5 animate-pulse rounded-sm" />}
    </div>
  );
}

// Message assistant statique (historique, pas d'animation)
function StaticMarkdown({ content }) {
  return (
    <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1.5 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-slate-900 prose-code:text-[var(--ia-blue)] prose-code:bg-blue-50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs">
      <ReactMarkdown>{content}</ReactMarkdown>
    </div>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [quota, setQuota] = useState(null);
  const [streamingIdx, setStreamingIdx] = useState(-1);
  const messagesEnd = useRef(null);

  useEffect(() => { loadConversations(); loadQuota(); }, []);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streamingIdx]);
  useEffect(() => { if (activeConv) { loadMessages(activeConv); setStreamingIdx(-1); } }, [activeConv]);

  const loadConversations = async () => {
    try { const { data } = await API.get("/conversations"); setConversations(data); } catch {}
  };

  const loadMessages = async (convId) => {
    try { const { data } = await API.get(`/chat/${convId}`); setMessages(data); } catch {}
  };

  const loadQuota = async () => {
    try { const { data } = await API.get("/user/quota/chat"); setQuota(data); } catch {}
  };

  const handleStreamComplete = useCallback(() => {
    setStreamingIdx(-1);
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: "user", content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    setStreamingIdx(-1);
    try {
      const { data } = await API.post("/chat", { message: text, conversation_id: activeConv });
      if (!activeConv) {
        setActiveConv(data.conversation_id);
        loadConversations();
      }
      const newMsg = { role: "assistant", content: data.response, created_at: new Date().toISOString() };
      setMessages(prev => {
        const newMessages = [...prev, newMsg];
        setStreamingIdx(newMessages.length - 1);
        return newMessages;
      });
      loadQuota();
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur du service IA";
      toast.error(typeof detail === "string" ? detail : "Erreur");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const newConversation = () => { setActiveConv(null); setMessages([]); setStreamingIdx(-1); };

  const deleteConv = async (convId, e) => {
    e.stopPropagation();
    try {
      await API.delete(`/conversations/${convId}`);
      if (activeConv === convId) { setActiveConv(null); setMessages([]); }
      loadConversations();
    } catch {}
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const quotaExceeded = quota && !quota.allowed;

  return (
    <div className="flex h-full" data-testid="chat-page">
      {/* Sidebar - conversations */}
      <div className="hidden md:flex w-64 border-r border-[var(--ia-border)] bg-white flex-col">
        <div className="p-3 border-b border-[var(--ia-border)]">
          <Button onClick={newConversation} className="w-full bg-[var(--ia-navy)] text-sm" size="sm" data-testid="new-conversation-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Nouvelle conversation
          </Button>
        </div>
        <ScrollArea className="flex-1 p-2">
          {conversations.length === 0 ? (
            <p className="text-xs text-[var(--ia-muted)] text-center mt-8 px-4">Commencez une conversation avec l'IA</p>
          ) : (
            <div className="space-y-1">
              {conversations.map(c => (
                <button key={c.id} onClick={() => setActiveConv(c.id)} data-testid={`conv-${c.id}`}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm group transition-all ${activeConv === c.id ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                  <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                  <span className="flex-1 truncate">{c.title}</span>
                  <button onClick={(e) => deleteConv(c.id, e)} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-conv-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
                  </button>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
        {quota && (
          <div className="p-3 border-t border-[var(--ia-border)] text-xs text-[var(--ia-muted)]" data-testid="chat-quota-info">
            {quota.current}/{quota.limit} messages aujourd'hui
            {user?.role === "free" && <span className="block text-amber-600 mt-1 cursor-pointer hover:underline">Passez en Premium pour plus</span>}
          </div>
        )}
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {messages.length === 0 && !loading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-lg text-center animate-fade-in">
              <div className="w-16 h-16 bg-[var(--ia-navy)]/5 rounded-full flex items-center justify-center mx-auto mb-6">
                <Sparkles className="h-8 w-8 text-[var(--ia-navy)]" />
              </div>
              <h2 className="text-2xl font-bold mb-2 text-[var(--ia-text)]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>IA Conforme</h2>
              <p className="text-[var(--ia-text-muted)] mb-6">Posez vos questions sur la conformité RGPD et l'AI Act</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTED_QUESTIONS.slice(0, 4).map((q, i) => (
                  <button key={i} onClick={() => sendMessage(q)} data-testid={`suggested-q-${i}`}
                    className="text-left text-sm p-3 rounded-md border border-[var(--ia-border)] bg-white hover:border-[var(--ia-blue)] hover:bg-blue-50/50 transition-all duration-200">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ScrollArea className="flex-1 p-4">
            <div className="max-w-3xl mx-auto space-y-4">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-fade-in`}>
                  {msg.role === "user" ? (
                    <div className="max-w-[85%] rounded-md px-4 py-3 text-sm bg-[var(--ia-navy)] text-white" data-testid={`chat-msg-${i}`}>
                      {msg.content}
                    </div>
                  ) : (
                    <div className="max-w-[85%] rounded-md px-4 py-3 text-sm bg-white border border-[var(--ia-border)] text-slate-800" data-testid={`chat-msg-${i}`}>
                      {i === streamingIdx ? (
                        <StreamingText content={msg.content} onComplete={handleStreamComplete} />
                      ) : (
                        <StaticMarkdown content={msg.content} />
                      )}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start animate-fade-in">
                  <div className="bg-white border border-[var(--ia-border)] rounded-md px-4 py-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 bg-[var(--ia-navy)] rounded-full typing-dot" />
                    <div className="w-2 h-2 bg-[var(--ia-navy)] rounded-full typing-dot" />
                    <div className="w-2 h-2 bg-[var(--ia-navy)] rounded-full typing-dot" />
                  </div>
                </div>
              )}
              <div ref={messagesEnd} />
            </div>
          </ScrollArea>
        )}

        {/* Input */}
        <div className="border-t border-[var(--ia-border)] bg-white p-4">
          <div className="max-w-3xl mx-auto">
            {quotaExceeded ? (
              <div className="flex items-center gap-3 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800" data-testid="chat-quota-exceeded">
                <Crown className="h-5 w-5 flex-shrink-0" />
                <span>Limite quotidienne atteinte. Passez en Premium pour continuer.</span>
              </div>
            ) : (
              <div className="flex gap-2">
                <Textarea data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="Posez votre question sur la conformité IA..." rows={1}
                  className="resize-none min-h-[42px] max-h-32" disabled={loading} />
                <Button onClick={() => sendMessage(input)} disabled={!input.trim() || loading} data-testid="chat-send-btn" className="bg-[var(--ia-navy)] px-3">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
